import { api } from '@/lib/api';

/** Matches the delay used by the other CSV exports in this codebase. */
const REVOKE_DELAY_MS = 60_000;

export interface ReportsKpis {
  total_staff: number;
  attendance_rate: number;
  leaves_approved: number;
  active_assets: number;
  purchase_debit: number;
  purchase_credit: number;
}

export interface AttendanceRangeSummary {
  present: number;
  late: number;
  half_day: number;
  absent: number;
}

export interface AttendanceTop10Item {
  user_id: string;
  name: string;
  employee_code: string;
  photo_url: string | null;
  department_name: string | null;
  total_minutes: number;
  days_present: number;
}

export interface AttendanceEmployeeItem {
  user_id: string;
  name: string;
  employee_code: string;
  photo_url: string | null;
  department_name: string | null;
  total_minutes: number;
  days_present: number;
  days_late: number;
  days_absent: number;
}

export interface AttendanceEmployeeListResult {
  data: AttendanceEmployeeItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface AttendanceDailyTrendPoint { date: string; present: number; late: number; absent: number; }
export interface DailyTrendPoint { date: string; count: number; }
export interface DeptTrendPoint { department: string; count: number; }
export interface LeaveTypeDist { type_name: string; color: string | null; count: number; }
export interface AssetStatusDist { status_name: string; status_code: string; color: string | null; count: number; }
export interface AssetDeptDist { department: string; count: number; }
export interface PurchaseDailyPoint { date: string; debit: number; credit: number; }
export interface PurchaseCategoryRow { category: string; total: number; }
export interface StaffDeptDist { department: string; count: number; }
export interface StaffDesignationRow { designation: string; count: number; pct: number; }
export interface TaskCompletionPoint { date: string; completed: number; }
export interface TaskDeptStatus {
  department: string;
  pending_accept: number;
  in_progress: number;
  completed: number;
  discarded: number;
}

export interface TaskEmployeeStatus {
  employee_name: string;
  employee_code: string | null;
  department_name: string;
  pending_accept: number;
  in_progress: number;
  completed: number;
  discarded: number;
}

/** One row per employee for the Leave tab's table. */
export interface LeaveEmployeeItem {
  user_id: string;
  name: string;
  employee_code: string | null;
  department_name: string | null;
  /** Approved full-leave requests (`leave_requests`). */
  request_count: number;
  /** Sum of `days_count` — calendar days as applied, so 0.5 for a half-day. */
  total_days: number;
  lop_days: number;
  /** Approved short leaves, counted separately since they have no day value. */
  short_count: number;
  short_minutes: number;
  last_leave_date: string | null;
}

export interface LeaveEmployeeResult {
  data: LeaveEmployeeItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

// ── Goal Report ───────────────────────────────────────────────────────────────

export type GoalReportStatus =
  | 'draft' | 'active' | 'submitted' | 'achieved' | 'missed' | 'cancelled';

export interface GoalReportSummary {
  total: number;
  draft: number;
  active: number;
  submitted: number;
  achieved: number;
  missed: number;
  cancelled: number;
  overdue: number;
  at_risk: number;
  avg_progress: number;
  avg_rating: number;
  achievement_rate: number;
}

export interface GoalStatusCount {
  status: GoalReportStatus;
  count: number;
}

export interface GoalDeptBreakdownItem {
  department: string;
  total: number;
  achieved: number;
  active: number;
  missed: number;
  overdue: number;
  avg_progress: number;
  achievement_rate: number;
}

export interface GoalCategoryBreakdownItem {
  category: string;
  total: number;
  achieved: number;
  avg_progress: number;
}

export interface GoalTrendPoint {
  /** Monday of the ISO week, as YYYY-MM-DD. */
  period: string;
  achieved: number;
  missed: number;
}

export interface GoalEmployeeScoreItem {
  user_id: string;
  name: string;
  employee_code: string | null;
  department_name: string | null;
  total: number;
  achieved: number;
  active: number;
  overdue: number;
  at_risk: number;
  avg_progress: number;
  avg_rating: number;
}

export interface GoalScorecardResult {
  data: GoalEmployeeScoreItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface ReportsFilter {
  date_from?: string;
  date_to?: string;
  department_id?: string;
}

export const reportsService = {
  async getKpis(filter: ReportsFilter): Promise<ReportsKpis> {
    const res = await api.get<{ data: ReportsKpis }>('/reports/kpis', { params: filter });
    return res.data.data;
  },

  async getAttendanceRangeSummary(filter: ReportsFilter): Promise<AttendanceRangeSummary> {
    const res = await api.get<{ data: AttendanceRangeSummary }>('/reports/attendance/range-summary', { params: filter });
    return res.data.data;
  },

  async getAttendanceTop10(filter: ReportsFilter): Promise<AttendanceTop10Item[]> {
    const res = await api.get<{ data: AttendanceTop10Item[] }>('/reports/attendance/top10', { params: filter });
    return res.data.data;
  },

  /** `search` matches the employee's full name or employee code. */
  async getAttendanceEmployeeList(
    filter: ReportsFilter,
    page: number,
    search?: string,
    limit = 20,
  ): Promise<AttendanceEmployeeListResult> {
    const res = await api.get<AttendanceEmployeeListResult>('/reports/attendance/employees', {
      params: { ...filter, q: search || undefined, page, limit },
    });
    return res.data;
  },

  async getAttendanceDailyTrend(filter: ReportsFilter): Promise<AttendanceDailyTrendPoint[]> {
    const res = await api.get<{ data: AttendanceDailyTrendPoint[] }>('/reports/attendance/daily-trend', { params: filter });
    return res.data.data;
  },

  async getAttendanceDeptTrend(filter: ReportsFilter): Promise<DeptTrendPoint[]> {
    const res = await api.get<{ data: DeptTrendPoint[] }>('/reports/attendance/dept-trend', { params: filter });
    return res.data.data;
  },

  async getLeaveDailyTrend(filter: ReportsFilter): Promise<DailyTrendPoint[]> {
    const res = await api.get<{ data: DailyTrendPoint[] }>('/reports/leave/daily-trend', { params: filter });
    return res.data.data;
  },

  async getLeaveTypeDistribution(filter: ReportsFilter): Promise<LeaveTypeDist[]> {
    const res = await api.get<{ data: LeaveTypeDist[] }>('/reports/leave/type-distribution', { params: filter });
    return res.data.data;
  },

  /** `search` matches the employee's full name or employee code. */
  async getLeaveEmployeeList(
    filter: ReportsFilter,
    page: number,
    search?: string,
    limit = 20,
  ): Promise<LeaveEmployeeResult> {
    const res = await api.get<LeaveEmployeeResult>('/reports/leave/employees', {
      params: { ...filter, q: search || undefined, page, limit },
    });
    return res.data;
  },

  async getAssetStatusDistribution(): Promise<AssetStatusDist[]> {
    const res = await api.get<{ data: AssetStatusDist[] }>('/reports/assets/status-distribution');
    return res.data.data;
  },

  async getAssetDeptDistribution(): Promise<AssetDeptDist[]> {
    const res = await api.get<{ data: AssetDeptDist[] }>('/reports/assets/dept-distribution');
    return res.data.data;
  },

  async getPurchaseDailyTrend(filter: ReportsFilter): Promise<PurchaseDailyPoint[]> {
    const res = await api.get<{ data: PurchaseDailyPoint[] }>('/reports/purchase/daily-trend', { params: filter });
    return res.data.data;
  },

  async getPurchaseCategoryBreakdown(filter: ReportsFilter): Promise<PurchaseCategoryRow[]> {
    const res = await api.get<{ data: PurchaseCategoryRow[] }>('/reports/purchase/category-breakdown', { params: filter });
    return res.data.data;
  },

  async getStaffDeptDistribution(filter: ReportsFilter): Promise<StaffDeptDist[]> {
    const res = await api.get<{ data: StaffDeptDist[] }>('/reports/staff/dept-distribution', { params: { department_id: filter.department_id } });
    return res.data.data;
  },

  async getStaffDesignationBreakdown(filter: ReportsFilter): Promise<StaffDesignationRow[]> {
    const res = await api.get<{ data: StaffDesignationRow[] }>('/reports/staff/designation-breakdown', { params: { department_id: filter.department_id } });
    return res.data.data;
  },

  async getTaskCompletionTrend(filter: ReportsFilter): Promise<TaskCompletionPoint[]> {
    const res = await api.get<{ data: TaskCompletionPoint[] }>('/reports/tasks/completion-trend', { params: filter });
    return res.data.data;
  },

  async getTaskStatusByDept(filter: ReportsFilter): Promise<TaskDeptStatus[]> {
    const res = await api.get<{ data: TaskDeptStatus[] }>('/reports/tasks/status-by-dept', { params: filter });
    return res.data.data;
  },

  async getTaskStatusByEmployee(filter: ReportsFilter): Promise<TaskEmployeeStatus[]> {
    const res = await api.get<{ data: TaskEmployeeStatus[] }>('/reports/tasks/status-by-employee', { params: filter });
    return res.data.data;
  },

  // ── Goal Report ─────────────────────────────────────────────────────────────

  async getGoalSummary(filter: ReportsFilter): Promise<GoalReportSummary> {
    const res = await api.get<{ data: GoalReportSummary }>('/reports/goals/summary', { params: filter });
    return res.data.data;
  },

  async getGoalStatusDistribution(filter: ReportsFilter): Promise<GoalStatusCount[]> {
    const res = await api.get<{ data: GoalStatusCount[] }>('/reports/goals/status-distribution', { params: filter });
    return res.data.data;
  },

  async getGoalDeptBreakdown(filter: ReportsFilter): Promise<GoalDeptBreakdownItem[]> {
    const res = await api.get<{ data: GoalDeptBreakdownItem[] }>('/reports/goals/dept-breakdown', { params: filter });
    return res.data.data;
  },

  async getGoalCategoryBreakdown(filter: ReportsFilter): Promise<GoalCategoryBreakdownItem[]> {
    const res = await api.get<{ data: GoalCategoryBreakdownItem[] }>('/reports/goals/category-breakdown', { params: filter });
    return res.data.data;
  },

  async getGoalCompletionTrend(filter: ReportsFilter): Promise<GoalTrendPoint[]> {
    const res = await api.get<{ data: GoalTrendPoint[] }>('/reports/goals/completion-trend', { params: filter });
    return res.data.data;
  },

  async getGoalEmployeeScorecard(filter: ReportsFilter, page: number, limit = 20): Promise<GoalScorecardResult> {
    const res = await api.get<GoalScorecardResult>('/reports/goals/employees', {
      params: { ...filter, page, limit },
    });
    return res.data;
  },

  /**
   * Downloads the employee scorecard as CSV. Sends the same filter as the table
   * — the backend ignores page/limit and returns every matching row.
   */
  async exportGoalCsv(filter: ReportsFilter): Promise<void> {
    const res = await api.get('/reports/goals/export/csv', { params: filter, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'text/csv' }));
    const today = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `goal-report-${today}.csv`;
    // anchor must be in the document for the click to register in some browsers
    document.body.appendChild(a);
    a.click();
    a.remove();
    // defer revocation so a slow browser still has the blob when it commits the file
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  },
};
